/**
 * Compact-layout bottom sheet (issue #109, made hamburger-only by #132): while
 * the compact layout signal (`ui/layoutMode.ts`'s `createCompactSignal`) is
 * active, moves a fixed set of existing, LIVE HUD elements into a `.card`
 * sheet and restores every one of them to its original DOM slot the moment
 * compact layout ends. Re-parenting moves the real nodes rather than cloning
 * them, so every listener and every bit of persisted state
 * (`ui/hudPrefs.ts`) keeps working untouched, and no control ever exists
 * twice — `main.ts`'s existing wiring needs no compact-specific branch.
 * Issue #132 pushed the last remaining persistent chrome (the mode control,
 * viewpoint select, and the collapsed caveat row) into `items` too, so the
 * only thing left outside the sheet while compact is the hamburger toggle
 * itself and the minimap.
 *
 * `#hud-strip` (the readout + status strip) is tracked apart from the other
 * items: while compact, a BLOCKER status (hard constraint #7's escape hatch)
 * pulls it OUT of the sheet into a full-width banner at the front of
 * `#hud-bottom`, regardless of whether the sheet itself is open or closed —
 * a missing-input report must never be buried in a closed sheet.
 * `setBlocker()` is the caller's hook for that; this module never inspects
 * `#status` text itself.
 *
 * Every move targets a stable anchor rather than a remembered sibling
 * reference, so a repeated enter/leave round-trip is exact and idempotent:
 *  - entering/leaving compact pins each element's current DOM position with
 *    an adjacent marker comment node, and restores it there later — correct
 *    even if unrelated siblings changed while compact was active.
 *  - the hud-strip banner pull-out/return toggles between two ALWAYS-present
 *    real locations (the end of the sheet's content, and the front of the
 *    banner parent), so it can flip any number of times without
 *    accumulating markers.
 */

export interface MobileSheetConfig {
  /** The sheet container (the issue's `.card` element) — toggled open/closed. */
  sheetEl: HTMLElement;
  /** Scrollable content region inside `sheetEl` that items are appended to. */
  contentEl: HTMLElement;
  /** "More" button (bottom-left) that opens the sheet. */
  toggleEl: HTMLElement;
  /** Close button inside the sheet. */
  closeEl: HTMLElement;
  /** Tapping this element (the scene canvas) closes the sheet. */
  sceneEl: HTMLElement;
  /** Elements moved into the sheet on entering compact, in display order.
   * Does not include `hudStripEl` — see the class doc. */
  items: readonly HTMLElement[];
  /** The readout/status strip — see the class doc for its special handling.
   * Always appended last within the sheet's content (issue #132's required
   * order puts it after every other item, including the caveat). */
  hudStripEl: HTMLElement;
  /** Parent `hudStripEl` is inserted into when pulled out as a BLOCKER
   * banner (`#hud-bottom`) — inserted at the FRONT of this element's
   * existing children, so it needs no dedicated anchor element of its own. */
  bannerParentEl: HTMLElement;
}

export class MobileSheet {
  private readonly sheetEl: HTMLElement;
  private readonly contentEl: HTMLElement;
  private readonly toggleEl: HTMLElement;
  private readonly closeEl: HTMLElement;
  private readonly sceneEl: HTMLElement;
  private readonly items: readonly HTMLElement[];
  private readonly hudStripEl: HTMLElement;
  private readonly bannerParentEl: HTMLElement;

  /** Restore callbacks keyed by the element they restore, one per currently
   * pinned element. Empty whenever not compact. */
  private readonly anchors = new Map<HTMLElement, () => void>();
  private readonly listeners = new Set<(open: boolean) => void>();

  private compact = false;
  private open = false;
  private blocker = false;

  constructor(config: MobileSheetConfig) {
    this.sheetEl = config.sheetEl;
    this.contentEl = config.contentEl;
    this.toggleEl = config.toggleEl;
    this.closeEl = config.closeEl;
    this.sceneEl = config.sceneEl;
    this.items = config.items;
    this.hudStripEl = config.hudStripEl;
    this.bannerParentEl = config.bannerParentEl;

    this.toggleEl.addEventListener('click', () => this.setOpen(!this.open));
    this.closeEl.addEventListener('click', () => this.setOpen(false));
    this.sceneEl.addEventListener('click', () => this.setOpen(false));
    this.render();
  }

  /** Whether the compact (slim top row + sheet) layout is currently active. */
  get isCompact(): boolean {
    return this.compact;
  }

  /** Whether the sheet is currently open. Always false while not compact. */
  get isOpen(): boolean {
    return this.open;
  }

  /**
   * Whether `#hud-strip` — and hence the per-frame readout it contains — is
   * currently reachable without any further user action: always true on
   * desktop, and true on a phone whenever the sheet is open OR a BLOCKER
   * banner is showing. `main.ts`'s render loop uses this to skip the
   * per-frame `readoutEl.textContent` write while it would land on a hidden
   * node (repo performance rule: no wasted per-frame DOM writes).
   */
  get hudStripVisible(): boolean {
    return !this.compact || this.open || this.blocker;
  }

  /**
   * Registers a listener for open/close transitions, so an exclusive-overlay
   * peer (issue #110's minimap expand, issue #111's route strip) can close
   * itself when this sheet opens, or ask this sheet to close via
   * `setOpen(false)` when IT opens. Fires only on a real transition,
   * mirroring `LayoutSignal.subscribe` in `layoutMode.ts`. Returns an
   * unsubscribe function.
   */
  subscribe(listener: (open: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Applies the compact/desktop layout signal. Idempotent: a repeated call
   * with the same value (e.g. a portrait-to-landscape rotation that stays
   * compact via the height clause either side, so the underlying signal
   * never actually changes) never re-parents anything a second time.
   */
  setCompact(compact: boolean): void {
    if (compact === this.compact) return;
    if (compact) {
      this.compact = true;
      this.enterCompact();
    } else {
      this.leaveCompact();
      this.compact = false;
    }
    this.render();
  }

  /**
   * Opens or closes the sheet. A no-op while not compact — desktop has no
   * closed sheet to open, so a stray call (e.g. a click queued just before a
   * resize back to desktop) can never surface it there.
   */
  setOpen(open: boolean): void {
    if (!this.compact || open === this.open) return;
    this.open = open;
    this.render();
    this.notify();
  }

  /**
   * Reflects whether `#status` currently reads a BLOCKER message (hard
   * constraint #7). See the class doc for the pull-out behaviour. A no-op
   * while not compact — `#hud-strip` never left its desktop slot there, and
   * its existing `status--blocker` styling already covers the message.
   */
  setBlocker(blocker: boolean): void {
    if (blocker === this.blocker) return;
    this.blocker = blocker;
    if (!this.compact) return;
    this.placeHudStrip();
  }

  private enterCompact(): void {
    for (const el of this.items) {
      this.pin(el);
      this.contentEl.appendChild(el);
    }
    this.pin(this.hudStripEl);
    this.placeHudStrip();
  }

  private leaveCompact(): void {
    if (this.open) {
      this.open = false;
      this.notify();
    }
    this.restore(this.hudStripEl);
    for (const el of this.items) this.restore(el);
  }

  /** Puts `#hud-strip` wherever it currently belongs: the front of the
   * banner parent while a BLOCKER is showing, else the end of the sheet's
   * content (issue #132's required order: every other item, then the
   * caveat, then the readout/status strip last). Safe to call from either
   * location — `insertBefore`/`appendChild` detach the node from wherever it
   * currently lives. */
  private placeHudStrip(): void {
    if (this.blocker) {
      this.bannerParentEl.insertBefore(this.hudStripEl, this.bannerParentEl.firstChild);
    } else {
      this.contentEl.appendChild(this.hudStripEl);
    }
  }

  private pin(el: HTMLElement): void {
    const marker = document.createComment('mobile-sheet-anchor');
    el.parentNode?.insertBefore(marker, el);
    this.anchors.set(el, () => {
      marker.parentNode?.insertBefore(el, marker);
      marker.remove();
    });
  }

  private restore(el: HTMLElement): void {
    const detach = this.anchors.get(el);
    if (!detach) return;
    this.anchors.delete(el);
    detach();
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.open));
  }

  private render(): void {
    this.sheetEl.classList.toggle('hidden', !this.open);
    this.toggleEl.setAttribute('aria-expanded', String(this.open));
    this.toggleEl.classList.toggle('active', this.open);
  }
}
